import { CobbAngleTool, annotation as annotationModule, drawing } from "@cornerstonejs/tools";

/**
 * CobbAngleTool that also labels the **line-to-line distance** — the distance
 * between the centers (midpoints) of the two Cobb lines — directly ON the dotted
 * link line, while the angle stays in the tool's own linked textbox.
 *
 * Why a subclass: Cornerstone's Cobb shows a single linked textbox (the angle).
 * Putting the distance there too stacks "angle / distance" in one box; the
 * radiologists want the distance next to the dashed connector (which is what
 * the distance actually measures). MedDream renders it the same way. We keep the
 * built-in toolName ("CobbAngle") so the save/load (PR encode/decode) and tool
 * group wiring are unchanged — this class simply replaces the registered tool.
 *
 * The distance is computed from world-space handle points, so it is in
 * millimetres on spatially-calibrated images (same assumption as LengthTool).
 */
class CobbAngleWithDistanceTool extends CobbAngleTool {
  constructor(props) {
    super(props);
    // renderAnnotation is an instance arrow-function on the base tool; wrap it so
    // the native render runs first (arms + dotted link line + angle textbox),
    // then we overlay the distance label on the link line.
    const baseRender = this.renderAnnotation;
    this.renderAnnotation = (enabledElement, svgDrawingHelper) => {
      const status = baseRender(enabledElement, svgDrawingHelper);
      try {
        this._renderDistanceLabels(enabledElement, svgDrawingHelper);
      } catch (_) {
        // Never let the extra label break the native Cobb render.
      }
      return status;
    };
  }

  _renderDistanceLabels(enabledElement, svgDrawingHelper) {
    const { viewport } = enabledElement;
    const { element } = viewport;
    let annotations = annotationModule.state.getAnnotations(this.getToolName(), element);
    if (!annotations?.length) return;
    annotations = this.filterInteractableAnnotationsForElement(element, annotations);
    if (!annotations?.length) return;

    const mid3 = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
    const mid2 = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];

    for (let i = 0; i < annotations.length; i++) {
      const ann = annotations[i];
      const { annotationUID, data } = ann;
      const pts = data?.handles?.points;
      if (!pts || pts.length < 4) continue;
      if (annotationModule.visibility?.isAnnotationVisible &&
          !annotationModule.visibility.isAnnotationVisible(annotationUID)) {
        continue;
      }

      // Distance between the two lines' midpoints, in world units (= mm).
      const wm1 = mid3(pts[0], pts[1]);
      const wm2 = mid3(pts[2], pts[3]);
      const dist = Math.hypot(wm2[0] - wm1[0], wm2[1] - wm1[1], wm2[2] - wm1[2]);
      if (!isFinite(dist) || dist <= 0) continue;

      // Place the label at the centre of the dotted link line (canvas space).
      const c = pts.map((p) => viewport.worldToCanvas(p));
      const cm1 = mid2(c[0], c[1]);
      const cm2 = mid2(c[2], c[3]);
      const centre = mid2(cm1, cm2);

      const styleSpecifier = {
        toolGroupId: this.toolGroupId,
        toolName: this.getToolName(),
        viewportId: viewport.id,
        annotationUID,
      };
      let color = "rgb(255, 255, 0)";
      try {
        color = this.getAnnotationStyle({ annotation: ann, styleSpecifier }).color || color;
      } catch (_) { /* keep default */ }

      // textUID "cobbDistance" is distinct from the native "cobbAngleText" box.
      drawing.drawTextBox(
        svgDrawingHelper,
        annotationUID,
        "cobbDistance",
        [`${dist.toFixed(2)} mm`],
        centre,
        { color, centerX: true, centerY: true, padding: 3 }
      );
    }
  }
}

export default CobbAngleWithDistanceTool;
